/**
 * The `supported_payment_method_type.paymentMethodType` value that marks the
 * Account Funds method (PD-14047). Shared because both the server (fee
 * calculation, settings validation) and the settings UI have to recognise it:
 * Account Funds redirects money the tenant already holds, so it can never
 * carry a processing fee (PD-15082).
 */
export const ACCOUNT_FUNDS_METHOD_TYPE = 'Account Funds';

export interface ClientAllowedPaymentMethod {
	isAllowed: boolean;
	feeUnit?: '$' | '%';
	feeAmount?: number;
}

/**
 * Runtime list of every method key the client blob can configure —
 * the single source for validators and UIs that need to enumerate
 * the known keys (e.g. server-side blob validation and seeding a
 * new blob with explicit allows, PD-15063). The
 * `ClientAllowedPaymentMethodKey` union is derived from this array
 * so the two can never drift.
 */
export const CLIENT_ALLOWED_PAYMENT_METHOD_KEYS = [
	'companyCredit',
	'creditCard',
	'ach',
	'cash',
	'check',
] as const;

/**
 * The canonical set of payment-method-type identifiers a client
 * row can carry constraints for. Kept as its own named union so
 * that `keyof ClientAllowedPaymentMethods` — which also includes
 * the `forced` metadata key — doesn't bleed into mapping helpers
 * that should only return method-type values.
 */
export type ClientAllowedPaymentMethodKey =
	(typeof CLIENT_ALLOWED_PAYMENT_METHOD_KEYS)[number];

/**
 * The method keys that can exist as SAVED payment-method rows on a
 * client (vaulted cards / bank accounts). Company Credit, Cash, and
 * Check are "recordless": they are checkout modes, never saved rows,
 * so no saved method can ever carry their type.
 *
 * PD-15707 — the forced-vs-primary agreement invariant only binds for
 * these keys. Requiring the saved primary to match a recordless forced
 * type is unsatisfiable (there is no row to mark primary), which
 * deadlocked forcing Company Credit on any client holding a card
 * primary. When `forced` is recordless, the saved-method primary is
 * orthogonal: the forced filter already excludes every saved method at
 * checkout, and the primary flag simply designates the default saved
 * method should the restriction change.
 */
export const SAVED_CLIENT_PAYMENT_METHOD_KEYS = [
	'creditCard',
	'ach',
] as const satisfies ReadonlyArray<ClientAllowedPaymentMethodKey>;

/**
 * Whether a forced key is one that saved payment-method rows can carry —
 * i.e. whether the forced-vs-primary agreement invariant applies to it.
 * Accepts `unknown` because callers commonly read the key out of the raw
 * JSON blob.
 */
export function isSavedClientPaymentMethodKey(key: unknown): boolean {
	return (
		typeof key === 'string' &&
		(SAVED_CLIENT_PAYMENT_METHOD_KEYS as ReadonlyArray<string>).includes(key)
	);
}

/**
 * Client-level payment-method policy. Two roles in one blob:
 *
 *  - Per-method config (the `companyCredit` / `creditCard` / etc.
 *    fields). When set, only methods whose entry has
 *    `isAllowed: true` are permitted at checkout; fee overrides
 *    can also be applied per method.
 *
 *  - Top-level `forced` field (added 2026-05-12). When non-null,
 *    that one method is the ONLY permitted type — overriding every
 *    per-entry `isAllowed` flag. Implicitly forces the chosen
 *    method into the "allowed" set even if its own entry says
 *    otherwise; the UI should auto-sync `isAllowed: true` on the
 *    forced entry to avoid a confusing apparent contradiction.
 *
 * If fees are not specified for a particular payment method, it
 * falls back to the payment gateway configuration. If a client
 * should explicitly have no fee, set the fee amount to 0.
 *
 * Replaces the legacy standalone `client.forcedPaymentMethodType`
 * column (dropped 2026-05-12). Single source of truth.
 */
// noinspection JSUnusedGlobalSymbols
export interface ClientAllowedPaymentMethods {
	companyCredit?: ClientAllowedPaymentMethod;
	creditCard?: ClientAllowedPaymentMethod;
	ach?: ClientAllowedPaymentMethod;
	cash?: ClientAllowedPaymentMethod;
	check?: ClientAllowedPaymentMethod;
	forced?: ClientAllowedPaymentMethodKey | null;
}

/**
 * Maps PaymentMethodTypeEnum string values (e.g. "Credit Card") to
 * ClientAllowedPaymentMethods keys (e.g. "creditCard").
 * Returns null for types that don't have client-level config (e.g. "Credit Card Reader").
 */
const paymentMethodTypeKeyMap: Record<string, ClientAllowedPaymentMethodKey> = {
	'Credit Card': 'creditCard',
	ACH: 'ach',
	'Company Credit': 'companyCredit',
	Cash: 'cash',
	Check: 'check',
};

export function getClientAllowedPaymentMethodKey(
	paymentMethodType: string,
): ClientAllowedPaymentMethodKey | null {
	return paymentMethodTypeKeyMap[paymentMethodType] ?? null;
}

/**
 * Returns the client-level config for a specific payment method type,
 * or null if the client has no config or the method isn't configured.
 */
export function getClientPaymentMethodConfig(
	allowedPaymentMethods: ClientAllowedPaymentMethods | null | undefined,
	paymentMethodType: string,
): ClientAllowedPaymentMethod | null {
	if (allowedPaymentMethods == null) {
		return null;
	}

	const key = getClientAllowedPaymentMethodKey(paymentMethodType);
	if (key == null) {
		return null;
	}

	return allowedPaymentMethods[key] ?? null;
}

/**
 * Authoritative server-side gate: returns true iff a payment method
 * type string (Title Case, e.g. 'Credit Card') is permitted for the
 * given client's `allowedPaymentMethods` blob.
 *
 * Semantics, in precedence order:
 *  1. `allowedPaymentMethods.forced` is a hard restriction — when
 *     set, only that one method key passes.
 *  2. `allowedPaymentMethods` per-entry flags are an allow-list:
 *     when the blob is present, types NOT explicitly allowed fail.
 *  3. When the blob is null (legacy clients), all method types
 *     pass and downstream gateway validation handles it.
 *
 * Types that have no entry in `paymentMethodTypeKeyMap`
 * (e.g. 'Account Funds', 'Credit Card Reader') get a `null` key —
 * they can't be restricted by the per-entry flags, so in allow-list
 * mode they pass through (the gateway itself decides whether
 * they're valid for the channel). Under `forced`, though, they are
 * rejected like every other non-forced type — see the inline
 * comment below.
 */
export function isPaymentTypeAllowedForClient(
	paymentMethodType: string,
	allowedPaymentMethods: ClientAllowedPaymentMethods | null | undefined,
): boolean {
	if (allowedPaymentMethods == null) {
		return true;
	}

	const key = getClientAllowedPaymentMethodKey(paymentMethodType);

	if (allowedPaymentMethods.forced != null) {
		// `forced` is a hard single-method allow-list: the client pays
		// with exactly that method and nothing else. Unmapped types
		// (key == null) are rejected here too — letting them through
		// would defeat the point of forcing a single method (PD-15063).
		return key === allowedPaymentMethods.forced;
	}

	if (key == null) {
		// Unmapped method type — the blob has no key that could allow
		// or deny it, so a blob's existence must not hide it. Defer to
		// downstream gateway validation (PD-15063).
		return true;
	}

	return allowedPaymentMethods[key]?.isAllowed === true;
}

/**
 * Requires at minimum paymentMethodType, processingFee, and processingFeeType fields.
 */
interface PaymentMethodWithFees {
	paymentMethodType: string;
	processingFee: number;
	processingFeeType: '%' | '$' | null;
}

/**
 * Filters an array of gateway payment methods by the client's allowed payment method config.
 *
 * - When allowedPaymentMethods is null/undefined, returns methods unchanged (no client restriction).
 * - When `forced` is set inside the blob, only that one method type survives (the per-entry
 *   isAllowed flags are ignored — forced wins). Unmapped types are dropped too: forced is a
 *   hard single-method allow-list.
 * - Otherwise, filters to only methods where isAllowed === true and applies fee overrides
 *   where the client config specifies feeAmount.
 * - Methods with no mapping in the client config (e.g. "Account Funds", "Credit Card Reader")
 *   pass through unchanged in allow-list mode — they can't be configured on the blob, so a
 *   blob's existence must not hide them (PD-15063). Mirrors `isPaymentTypeAllowedForClient`.
 */
export function filterPaymentMethodsByClientConfig<
	T extends PaymentMethodWithFees,
>(
	methods: Array<T>,
	allowedPaymentMethods: ClientAllowedPaymentMethods | null | undefined,
): Array<T> {
	// Null config means no client restrictions — use gateway config as-is
	if (allowedPaymentMethods == null) {
		return methods;
	}

	return methods
		.filter((method) => {
			const key = getClientAllowedPaymentMethodKey(method.paymentMethodType);
			// `forced` overrides everything: only that one method type
			// is permitted. Unmapped types (key == null) are dropped
			// here as well — forced means the client pays with exactly
			// that method, full stop (PD-15063).
			if (allowedPaymentMethods.forced != null) {
				return key === allowedPaymentMethods.forced;
			}
			// Unmapped method type — the blob has no key that could
			// allow or deny it, so it passes through and downstream
			// gateway validation decides (PD-15063).
			if (key == null) {
				return true;
			}
			const config = getClientPaymentMethodConfig(
				allowedPaymentMethods,
				method.paymentMethodType,
			);
			// Only include methods that are explicitly allowed by the client
			return config != null && config.isAllowed;
		})
		.map((method) => {
			const config = getClientPaymentMethodConfig(
				allowedPaymentMethods,
				method.paymentMethodType,
			);
			// Apply client fee overrides if feeAmount is defined
			if (config != null && config.feeAmount !== undefined) {
				return {
					...method,
					processingFee: config.feeAmount,
					processingFeeType: config.feeUnit ?? method.processingFeeType,
				};
			}
			return method;
		});
}

/**
 * Filters payment methods (either gateway-supported types or saved
 * client payment methods) down to only the one type matching the
 * given forced key. When the forced value is null, the input is
 * returned unchanged.
 *
 * Generic over the shape so it works for both:
 *   - SupportedPaymentMethodType (`paymentMethodType: string`)
 *   - PaymentMethod (`paymentType: string`)
 *
 * Pass `getTypeKey` to extract the Title-Case type string from the
 * row — the helper maps that to a ClientAllowedPaymentMethods key
 * via {@link getClientAllowedPaymentMethodKey} and compares to the
 * forced value.
 *
 * Callers usually extract `forcedType` from
 * `allowedPaymentMethods.forced`.
 */
export function filterByForcedPaymentMethodType<T>(
	methods: Array<T>,
	getTypeKey: (method: T) => string,
	forcedType: ClientAllowedPaymentMethodKey | null | undefined,
): Array<T> {
	if (forcedType == null) {
		return methods;
	}
	return methods.filter((method) => {
		const key = getClientAllowedPaymentMethodKey(getTypeKey(method));
		return key === forcedType;
	});
}
